import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MessageCircle, Search, Clock, ArrowLeft } from 'lucide-react';

const ChatList = () => {
  const navigate = useNavigate();
  const { user, language } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user) {
      fetchConversations();
    }
  }, [user]);

  const fetchConversations = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const token = localStorage.getItem('helply_token');
      
      const response = await fetch(`${API}/chat/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch conversations');
      
      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error(language === 'en' ? 'Failed to load conversations' : language === 'it' ? 'Impossibile caricare le conversazioni' : 'Gespräche konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString(language === 'en' ? 'en-US' : language === 'it' ? 'it-IT' : 'de-DE', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } else {
      return date.toLocaleDateString(language === 'en' ? 'en-US' : language === 'it' ? 'it-IT' : language === 'fr' ? 'fr-FR' : 'de-DE', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.jobTitle?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const content = {
    en: {
      title: 'Messages',
      search: 'Search conversations...',
      noChats: 'No conversations yet',
      noChatsDesc: 'Your conversations with clients will appear here',
      noResults: 'No conversations found',
      unread: 'unread'
    },
    de: {
      title: 'Nachrichten',
      search: 'Gespräche durchsuchen...',
      noChats: 'Noch keine Gespräche',
      noChatsDesc: 'Ihre Gespräche mit Kunden erscheinen hier',
      noResults: 'Keine Gespräche gefunden',
      unread: 'ungelesen'
    },
    fr: {
      title: 'Messages',
      search: 'Rechercher des conversations...',
      noChats: 'Aucune conversation encore',
      noChatsDesc: 'Vos conversations avec les clients apparaîtront ici',
      noResults: 'Aucune conversation trouvée',
      unread: 'non lu'
    },
    it: {
      title: 'Messaggi',
      search: 'Cerca conversazioni...',
      noChats: 'Nessuna conversazione ancora',
      noChatsDesc: 'Le tue conversazioni con i clienti appariranno qui',
      noResults: 'Nessuna conversazione trovata',
      unread: 'non letti'
    }
  };

  const t = content[language] || content.en;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Navigate back to appropriate dashboard based on user role
  const handleBack = () => {
    if (user?.role === 'professional') {
      navigate('/dashboard/professional');
    } else if (user?.is_admin) {
      navigate('/admin/dashboard');
    } else {
      navigate('/dashboard/client');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Fixed Header with Back Button */}
      <header className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleBack}
            className="flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">{language === 'en' ? 'Back' : language === 'it' ? 'Indietro' : 'Zurück'}</span>
          </Button>
          <img src="/helply-logo.jpg" alt="Helply" className="w-8 h-8 rounded-lg" />
          <h1 className="text-xl font-bold flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            {t.title}
          </h1>
        </div>
      </header>

      <div className="flex-1 py-6">
        <div className="max-w-4xl mx-auto px-4">
          <Card>
            <CardHeader>
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder={t.search}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>

          <CardContent>
            {filteredConversations.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  {searchQuery ? t.noResults : t.noChats}
                </h3>
                {!searchQuery && (
                  <p className="text-gray-500">{t.noChatsDesc}</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredConversations.map((conv) => (
                  <div
                    key={conv.jobId}
                    onClick={() => navigate(`/chat/${conv.jobId}`)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      conv.unreadCount > 0 ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-blue-600 text-white">
                          {conv.clientName?.charAt(0)?.toUpperCase() || 'C'}
                        </AvatarFallback>
                      </Avatar>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-semibold truncate ${
                              conv.unreadCount > 0 ? 'text-blue-900' : 'text-gray-900'
                            }`}>
                              {conv.clientName}
                            </h4>
                            <p className="text-sm text-gray-600 truncate">
                              {conv.jobTitle}
                            </p>
                          </div>
                          
                          {/* Time and Badge */}
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <Clock className="w-3 h-3" />
                              {formatDate(conv.lastMessageAt)}
                            </div>
                            {conv.unreadCount > 0 && (
                              <Badge variant="default" className="bg-blue-600">
                                {conv.unreadCount}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Last Message Preview */}
                        {conv.lastMessage && (
                          <p className={`text-sm mt-1 truncate ${
                            conv.unreadCount > 0 ? 'text-blue-800 font-medium' : 'text-gray-600'
                          }`}>
                            {conv.lastMessage}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default ChatList;
